var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Children, cloneElement, forwardRef, isValidElement, } from "react";
import cn from "classnames";
import { useCrossFade } from "./useCrossFade";
/**
 * The `Collapse` is really just a convenience wrapper for the `useCrossFade`
 * hook that triggers the transition by cloning the `ref` and `className` into
 * the `children` of this component.
 *
 * This transition will only fire on mount and when the `appear` prop is set to
 * `true`, so the way to trigger new animations is by changing the `key` for
 * this component so it re-mounts. However it is generally not recommended to
 * fire this transition on first page load especially when dealing with server
 * side rendering. A simple way to work around this is have the `CrossFade` near
 * the root of the app and just disable the `appear` prop until the first
 * render.
 *
 * If you want more fine-grain control over the transition, it is recommended to
 * use the `useCrossFade` hook instead.
 */
export var CrossFade = forwardRef(function CrossFade(_a, forwardedRef) {
    var _b = _a.wrap, wrap = _b === void 0 ? false : _b, _c = _a.appear, appear = _c === void 0 ? true : _c, _d = _a.temporary, temporary = _d === void 0 ? false : _d, propClassName = _a.className, _e = _a.transitionIn, transitionIn = _e === void 0 ? true : _e, children = _a.children, onEnter = _a.onEnter, onEntering = _a.onEntering, onEntered = _a.onEntered, onExit = _a.onExit, onExiting = _a.onExiting, onExited = _a.onExited, props = __rest(_a, ["wrap", "appear", "temporary", "className", "transitionIn", "children", "onEnter", "onEntering", "onEntered", "onExit", "onExiting", "onExited"]);
    var _f = useCrossFade({
        ref: forwardedRef,
        appear: appear,
        className: propClassName,
        onEnter: onEnter,
        onEntering: onEntering,
        onEntered: onEntered,
        onExit: onExit,
        onExiting: onExiting,
        onExited: onExited,
        temporary: temporary,
        transitionIn: transitionIn,
    }), rendered = _f[0], _g = _f[1], ref = _g.ref, className = _g.className;
    if (!rendered) {
        return null;
    }
    if (!wrap && isValidElement(children)) {
        var child = Children.only(children);
        return cloneElement(child, {
            ref: ref,
            className: cn(child.props.className, className),
        });
    }
    return (React.createElement("div", __assign({}, props, { className: className, ref: ref }), children));
});
/* istanbul ignore next */
if (process.env.NODE_ENV !== "production") {
    try {
        var PropTypes = require("prop-types");
        CrossFade.propTypes = {
            wrap: PropTypes.bool,
            appear: PropTypes.bool,
            temporary: PropTypes.bool,
            style: PropTypes.object,
            className: PropTypes.string,
            transitionIn: PropTypes.bool,
            onEnter: PropTypes.func,
            onEntering: PropTypes.func,
            onEntered: PropTypes.func,
            onExit: PropTypes.func,
            onExiting: PropTypes.func,
            onExited: PropTypes.func,
            children: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
        };
    }
    catch (e) { }
}
//# sourceMappingURL=CrossFade.js.map